// src/services/api.ts
import axios from "axios";
import type {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { parseCookies } from "nookies";

import { AuthTokenError } from "./errors/AuthTokenError";
import { signOut } from "../context/AuthContext";
import { getBackendUrl } from "../lib/backendUrl";
import { AUTH_COOKIE_NAME } from "../lib/authCookie";
import { normalizePathname, shouldTreat401AsAnonymous } from "../lib/publicRoutes";

interface ErrorResponse {
  error?: string;
  message?: string;
  statusCode?: number;
}

type ApiCtx = Parameters<typeof parseCookies>[0];

/**
 * Browser → BFF `/api/proxy` (cookie HttpOnly first-party).
 * SSR → backend direto com Bearer lido do cookie (HttpOnly disponível no req).
 */
export function setupAPIClient(ctx: ApiCtx = undefined) {
  const isBrowser = typeof window !== "undefined";
  const cookies = parseCookies(ctx);
  const token = cookies[AUTH_COOKIE_NAME];

  const useBff = isBrowser && ctx === undefined;
  const baseURL = useBff ? "/api/proxy" : getBackendUrl();

  const api = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
      ...(!useBff && token ? { Authorization: `Bearer ${token}` } : {}),
    },
    withCredentials: useBff,
  });

  const requestHandler = (config: InternalAxiosRequestConfig) => {
    try {
      if (!useBff) {
        const currentToken = parseCookies(ctx)[AUTH_COOKIE_NAME];
        if (currentToken && !config.headers.Authorization) {
          config.headers.set("Authorization", `Bearer ${currentToken}`);
        }
      }

      if (typeof window !== "undefined") {
        const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!config.headers.has("X-Client-Timezone")) {
          config.headers.set("X-Client-Timezone", clientTimezone);
        }

        if (
          config.url?.includes("schedule") ||
          config.url?.includes("available-times")
        ) {
          if (
            config.method === "post" &&
            config.data &&
            typeof config.data === "object"
          ) {
            config.data = {
              ...config.data,
              timezone: clientTimezone,
            };
          } else if (config.method?.toLowerCase() === "get") {
            config.params = {
              ...config.params,
              timezone: clientTimezone,
            };
          }
        }
      }

      if (config.method?.toLowerCase() === "get") {
        config.params = {
          ...config.params,
          _t: Date.now(),
        };
      }

      return config;
    } catch {
      return config;
    }
  };

  const requestErrorHandler = (error: unknown) => Promise.reject(error);

  const responseHandler = (response: AxiosResponse) => response;

  const responseErrorHandler = async (error: unknown) => {
    try {
      if (!axios.isAxiosError(error)) {
        throw new Error("Erro desconhecido");
      }

      const axiosError = error as AxiosError<ErrorResponse>;

      if (axiosError.response?.status === 429) {
        if (axiosError.config?.url?.includes("/session") ||
            axiosError.config?.url?.includes("/auth/login")) {
          interface RateLimitError extends Error {
            rateLimitExceeded: boolean;
          }
          const rateError = new Error(
            "Muitas tentativas de login. Aguarde um minuto antes de tentar novamente."
          ) as RateLimitError;
          rateError.rateLimitExceeded = true;
          throw rateError;
        }
      }

      const responseData = axiosError.response?.data;
      if (
        typeof responseData === "string" &&
        (responseData as string).indexOf("BigInt") >= 0
      ) {
        throw new Error("Erro no processamento de dados. Tente novamente.");
      }

      if (axiosError.code === "ECONNABORTED") {
        throw new Error("Tempo limite excedido. Tente novamente.");
      }

      if (axiosError.message === "Network Error") {
        throw new Error("Erro de conexão. Verifique sua internet.");
      }

      if (axiosError.response?.status === 401) {
        if (!isBrowser) {
          throw new AuthTokenError();
        }

        const currentPath = normalizePathname(window.location.pathname);

        // Visitante anônimo em rota pública: 401 é estado normal, não sessão expirada.
        if (shouldTreat401AsAnonymous(currentPath)) {
          return Promise.reject(axiosError);
        }

        // Rota protegida: sessão inválida ou expirada → encerrar e ir para login.
        try {
          signOut();
        } catch {
          // ignore
        }
        throw new Error("Sessão expirada. Por favor, faça login novamente.");
      }

      if (axiosError.response?.status === 400) {
        const errorMessage =
          axiosError.response.data?.error ||
          axiosError.response.data?.message ||
          "Dados inválidos";
        throw new Error(errorMessage);
      }

      if (axiosError.response?.status === 403) {
        throw new Error("Você não tem permissão para realizar esta ação");
      }

      if (axiosError.response?.status === 404) {
        throw new Error("Recurso não encontrado");
      }

      if (axiosError.response?.status === 500) {
        if (
          axiosError.response?.data?.error &&
          (String(axiosError.response.data.error).indexOf("BigInt") >= 0 ||
            String(axiosError.response.data.error).indexOf("serialize") >= 0)
        ) {
          throw new Error(
            "Erro na formatação de dados. Carregando dados simplificados."
          );
        }
        throw new Error(
          "Erro interno do servidor. Tente novamente mais tarde."
        );
      }

      const errorMessage =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Ocorreu um erro inesperado";
      throw new Error(errorMessage);
    } catch (handlerError) {
      if (handlerError instanceof Error) {
        return Promise.reject(handlerError);
      }
      return Promise.reject(new Error("Ocorreu um erro inesperado"));
    }
  };

  api.interceptors.request.use(requestHandler, requestErrorHandler);
  api.interceptors.response.use(responseHandler, responseErrorHandler);

  return api;
}
